import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';

import api from '../../services/api';
import { Carteira, Contato } from './types';

export const TIPO_CONTATO_CALLBACK = 4;

export const telMask = (value: string) => {
  let r = value.replace(/\D/g, '');
  r = r.replace(/^0/, '');
  if (r.length > 10) {
    r = r.replace(/^(\d\d)(\d{5})(\d{4}).*/, '($1) $2-$3');
  } else if (r.length > 5) {
    r = r.replace(/^(\d\d)(\d{4})(\d{0,4}).*/, '($1) $2-$3');
  } else if (r.length > 2) {
    r = r.replace(/^(\d\d)(\d{0,5})/, '($1) $2');
  } else {
    r = r.replace(/^(\d*)/, '($1');
  }
  return r;
};

export function useLandingPage(id?: string) {
  const [loading, setLoading] = useState(true);
  const [carteira, setCarteira] = useState<Carteira>();
  const [telValue, setTelValue] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [callbackAberto, setCallbackAberto] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const executarCaptcha = useCallback(async () => {
    const token = await recaptchaRef.current?.executeAsync();
    recaptchaRef.current?.reset();
    return token;
  }, []);

  const registrarContato = useCallback(
    async (tipoContatoId: number, telefone: string) => {
      const captcha = await executarCaptcha();

      return api.post('insere-contato-selecionado-shortlink', {
        captcha: captcha as string,
        carteiraCrmId: Number(carteira?.carteiraCrmId),
        idValue: id,
        telefone,
        tipoContatoId,
      });
    },
    [carteira?.carteiraCrmId, executarCaptcha, id]
  );

  const getContatos = useCallback(async () => {
    const response = await api.get(`busca-contato-shortlink/${id}`);

    if (response?.status === 200 && response?.data !== undefined) {
      const dados: Carteira = response.data[0];

      if (!dados.ativo) {
        window.location.replace('https://www.bellinatiperez.com.br');
        return;
      }

      if (dados.urlLogoClienteBase64) {
        dados.urlLogoClienteBase64 = `data:image/jpeg;base64,${dados.urlLogoClienteBase64}`;
      }

      setCarteira(dados);
    } else {
      toast.error(response?.data || 'Erro ao buscar contatos');
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (loading) getContatos();
  }, [getContatos, loading]);

  const onSelectContato = useCallback(
    (contato: Contato) => {
      if (contato.tipoContatoId === TIPO_CONTATO_CALLBACK) {
        setCallbackAberto(true);
        return;
      }

      // Redireciona os links
      window.open(contato.url);
      registrarContato(contato.tipoContatoId, telValue);
    },
    [registrarContato, telValue]
  );

  const onChangeTel = useCallback(
    (event: { target: { value: string } }) => setTelValue(telMask(event.target.value)),
    []
  );

  const onSubmitTel = useCallback(async () => {
    const response = await registrarContato(TIPO_CONTATO_CALLBACK, telValue);

    if (response?.data?.mensagem) setMensagem(response.data.mensagem);

    if (response?.status === 200 && response?.data?.telefone !== undefined) {
      setTelValue(response.data.telefone);
    } else {
      toast.error(response?.data || 'Erro ao inserir número');
    }
  }, [registrarContato, telValue]);

  const onFecharCallback = useCallback(() => {
    setCallbackAberto(false);
    setMensagem('');
  }, []);

  return {
    loading,
    carteira,
    contatos: carteira?.contatos ?? [],
    telValue,
    mensagem,
    callbackAberto,
    recaptchaRef,
    onSelectContato,
    onChangeTel,
    onSubmitTel,
    onFecharCallback,
  };
}
